import { Component, type ErrorInfo, type ReactNode } from 'react';

// Catches render-time exceptions anywhere in the tree. Without this, a
// thrown error inside a route component unmounts the whole React tree
// and the user sees a stark blank page (no header, no anything) —
// indistinguishable from a network failure. The boundary renders a
// short diagnostic and a reload link, and logs the error to the
// console for staff to grab from devtools.

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] uncaught render error:', error);
    console.error('[ErrorBoundary] componentStack:', info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: 'var(--color-surface-light)' }}
      >
        <div
          className="max-w-md w-full rounded-2xl bg-white p-8 text-center border"
          style={{ borderColor: 'var(--color-border)', boxShadow: 'var(--shadow-card)' }}
        >
          <h1 className="text-lg font-semibold text-[color:var(--color-text-primary)]">
            Something went wrong rendering this page
          </h1>
          <p className="mt-2 text-sm text-[color:var(--color-text-secondary)]">
            {this.state.error.message}
          </p>
          <p className="mt-3 text-xs text-[color:var(--color-text-tertiary)]">
            Reload to try again. If this keeps happening, share the URL and the
            error above with your Reputation X project manager.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[color:var(--color-brand-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
